import { Button, SearchBar, Select, type Column } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { companies, type Company } from "../../../api/addressBook.service";
import { DataTable } from "../../../components/DataTable/DataTable";
import { PageHeader } from "../../../components/PageHeader";
import PermissionGate from "../../../components/Permissions/PermissionGate";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { PaginationControls } from "../../../components/PaginationControls/PaginationControls";
import LoadingState from "../../../components/LoadingState";
import EmptyState from "../../../components/EmptyState";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { ROUTES } from "../../../routes";
import { debounce } from "../../../utils";
import styles from "../AddressBook.module.css";

function CompaniesPage() {
  const navigate = useNavigate();
  const { hasFlag } = useFeatureFlags();
  const isLive = hasFlag("address-book-live");
  const [searchParams, setSearchParams] = useSearchParams();
  const [displayedCompanies, setDisplayedCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(searchParams.get("search") || "");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get("pageSize")) || 10);

  const updateURL = useCallback(
    (updates: Record<string, string>, options?: { replace?: boolean }) => {
      const newParams = new URLSearchParams(window.location.search);
      Object.entries(updates).forEach(([key, value]) => {
        if (value && value !== "") {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams, options);
    },
    [setSearchParams],
  );

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: unknown) => {
        const searchValue = String(value);
        setAppliedSearchTerm(prev => {
          if (prev === searchValue) return prev;
          updateURL({ search: searchValue, page: "1" }, { replace: true });
          return searchValue;
        });
      }, 500),
    [updateURL],
  );

  useEffect(() => {
    const pageFromURL = Number(searchParams.get("page")) || 1;
    const pageSizeFromURL = Number(searchParams.get("pageSize")) || 10;
    const searchFromURL = searchParams.get("search") || "";
    if (pageFromURL !== currentPage) setCurrentPage(pageFromURL);
    if (pageSizeFromURL !== itemsPerPage) setItemsPerPage(pageSizeFromURL);
    if (searchFromURL !== appliedSearchTerm) {
      setSearchTerm(searchFromURL);
      setAppliedSearchTerm(searchFromURL);
    }
  }, [searchParams]);

  useEffect(() => {
    debouncedSetSearch(searchTerm);
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [searchTerm, debouncedSetSearch]);

  useEffect(() => {
    loadCompanies();
  }, [currentPage, itemsPerPage, appliedSearchTerm]);

  async function loadCompanies() {
    try {
      setLoading(true);
      setError(null);
      const offset = (currentPage - 1) * itemsPerPage;
      const response = await companies.getAll({
        search: appliedSearchTerm || undefined,
        limit: itemsPerPage,
        offset: offset,
      });
      setDisplayedCompanies(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to load companies");
      setError(error);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }

  const totalPages = Math.ceil(total / itemsPerPage);
  const offset = (currentPage - 1) * itemsPerPage;
  const startIndex = offset;
  const endIndex = offset + displayedCompanies.length;

  function handleCompanyClick(company: Company) {
    navigate(ROUTES.addressBook.companyDetail(String(company.id)));
  }

  function handleCreateClick() {
    navigate(ROUTES.addressBook.companyDetail("new"));
  }

  const columns: Column<Company>[] = [
    {
      header: "Firm Ref",
      accessor: "firmRef",
      render: (value: unknown) => (value as string) || "",
    },
    {
      header: "Name",
      accessor: "name",
    },
    {
      header: "Slug",
      accessor: "slug",
      render: (value: unknown) => (value as string) || "-",
    },
    {
      header: "CRM ID",
      accessor: "crmEntityNumber",
      render: (value: unknown) => (value as string) || "-",
    },
    {
      header: "Industry",
      accessor: "industry",
      render: (value: unknown) => (value as string) || "-",
    },
    {
      header: "Website",
      accessor: "website",
      render: (value: unknown) => (value as string) || "-",
    },
    {
      header: "Created",
      accessor: "createdAt",
      render: (value: unknown) => {
        const date = new Date(value as string);
        return date.toLocaleDateString();
      },
    },
  ];

  // Check for 401 Unauthorized or 403 Forbidden error
  if (error) {
    const status = (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;
    if (isAuthError) {
      return <UnauthorizedView />;
    }
  }

  if (loading && !hasLoadedOnce && !error) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Companies" subtitle="Manage company records in the address book" />
        <LoadingState message="Loading companies..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Companies" subtitle="Manage company records in the address book" />
        <EmptyState
          variant="error"
          title="Failed to load companies"
          message={error.message}
          action={{
            label: "Retry",
            onClick: loadCompanies,
          }}
        />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Companies" subtitle="Manage company records in the address book">
          {isLive && (
            <PermissionGate permission="addressbook.write">
              <Button variant="contained" color="primary" onClick={handleCreateClick}>
                Create Company
              </Button>
            </PermissionGate>
          )}
        </PageHeader>
      </div>

      <div className={styles.searchSection}>
        <SearchBar
          value={searchTerm}
          onChange={value => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          placeholder="Search by name or firm ref"
          onSubmit={e => {
            e.preventDefault();
            setAppliedSearchTerm(searchTerm);
            setCurrentPage(1);
            updateURL({ search: searchTerm, page: "1" }, { replace: true });
          }}
          onClear={() => {
            setSearchTerm("");
            setAppliedSearchTerm("");
            setCurrentPage(1);
            updateURL({ search: "", page: "1" }, { replace: true });
          }}
        />
      </div>

      <div className={styles.controlsSection}>
        <div className={styles.pageSizeSelector}>
          <Select
            label="Items per page"
            value={itemsPerPage}
            onChange={e => {
              const newSize = Number(e.target.value);
              setItemsPerPage(newSize);
              setCurrentPage(1);
              updateURL({ pageSize: String(newSize), page: "1" });
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            size="small"
          />
        </div>
      </div>

      <div className={styles.contentSection}>
        <DataTable
          data={displayedCompanies}
          columns={columns}
          onRowClick={handleCompanyClick}
          rowKey="id"
        />

        {displayedCompanies.length > 0 && (
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            totalCount={total}
            itemName="companies"
            onPageChange={page => {
              setCurrentPage(page);
              updateURL({ page: String(page) });
            }}
          />
        )}
      </div>
    </div>
  );
}

export default CompaniesPage;
